import { Button, useTranslation } from '@hbs-us/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureErrorBoundary, routeIdForPath } from './analytics.js';

/**
 * Route + query error boundary (D-04). Renders the UI-SPEC error-state copy in
 * an aria-live region with a keyboard-reachable "Try again". Fleshed out with
 * the full token-driven treatment in the shell build.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundaryInner extends Component<
  ErrorBoundaryProps & { t: (key: string) => string },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const routeId =
      typeof window === 'undefined' ? 'unknown' : routeIdForPath(window.location.pathname);
    captureErrorBoundary(error, info, routeId);
    if (import.meta.env.DEV) console.error('Dashboard error boundary caught:', error.name, routeId);
  }

  handleReset = (): void => {
    this.setState({ hasError: false });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { t } = this.props;
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display text-2xl font-semibold text-foreground">
          {t('common:error.heading')}
        </h1>
        <p className="max-w-sm text-sm text-muted-foreground">{t('common:error.body')}</p>
        <Button variant="secondary" onClick={this.handleReset}>
          {t('common:error.retry')}
        </Button>
      </div>
    );
  }
}

export function ErrorBoundary({ children, onReset }: ErrorBoundaryProps) {
  const { t } = useTranslation();
  return (
    <ErrorBoundaryInner t={t} onReset={onReset}>
      {children}
    </ErrorBoundaryInner>
  );
}
